<template>
  <div class="google-login-wrapper">
    <div class="auth-divider">
      <span>or</span>
    </div>

    <!-- Google Sign-In Button rendered by Google -->
    <div ref="googleButtonContainer" class="google-button-container"></div>

    <!-- Fallback loading state -->
    <div v-if="loading" class="btn-google btn-google-loading">
      <svg viewBox="0 0 24 24" width="18" height="18">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
      {{ loadingText }}
    </div>
  </div>
</template>

<script>
const GOOGLE_CLIENT_ID = '490927823674-mund6vaeein8j6vu60vgmku607vemr64.apps.googleusercontent.com'

export default {
  name: 'GoogleLoginButton',
  props: {
    mode: {
      type: String,
      default: 'login',
      validator: (value) => ['login', 'register'].includes(value)
    },
    buttonText: {
      type: String,
      default: 'Continue with Google'
    },
    loadingText: {
      type: String,
      default: 'Signing in...'
    }
  },
  data() {
    return {
      loading: false,
      googleLoaded: false
    }
  },
  mounted() {
    this.loadGoogleScript()
  },
  methods: {
    loadGoogleScript() {
      // Check if Google Identity Services is already loaded
      if (window.google?.accounts?.id) {
        this.initializeGoogleSignIn()
        return
      }

      // Load the Google Identity Services script
      const script = document.createElement('script')
      script.src = 'https://accounts.google.com/gsi/client'
      script.async = true
      script.defer = true
      script.onload = () => {
        this.initializeGoogleSignIn()
      }
      script.onerror = () => {
        this.$emit('error', 'Failed to load Google Sign-In. Please refresh the page.')
      }
      document.head.appendChild(script)
    },

    initializeGoogleSignIn() {
      if (!window.google?.accounts?.id) {
        return
      }

      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: this.handleCredentialResponse,
        auto_select: false,
        cancel_on_tap_outside: true
      })

      // Render the Google Sign-In button with responsive width
      const buttonWidth = window.innerWidth <= 360 ? 240 : window.innerWidth <= 480 ? 280 : 320
      window.google.accounts.id.renderButton(
        this.$refs.googleButtonContainer,
        {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: this.mode === 'login' ? 'signin_with' : 'signup_with',
          shape: 'rectangular',
          width: buttonWidth,
          logo_alignment: 'left'
        }
      )

      this.googleLoaded = true
    },

    async handleCredentialResponse(response) {
      if (!response.credential) {
        this.$emit('error', 'No credential received from Google')
        return
      }

      this.loading = true
      this.$emit('loading', true)
      this.$emit('error', '')

      try {
        // Decode the JWT token to get user info
        const googleUser = this.decodeJWT(response.credential)

        if (!googleUser.email) {
          throw new Error('Could not retrieve email from Google')
        }

        // Prepare data for backend
        const userData = {
          email: googleUser.email,
          first_name: googleUser.given_name || googleUser.email.split('@')[0],
          last_name: googleUser.family_name || '',
          google_id: googleUser.sub,
          avatar: googleUser.picture || null
        }

        // Send to backend
        await this.sendToBackend(userData)
      } catch (error) {
        console.error('Google auth error:', error)
        const errorMessage = error.message || 'Google authentication failed. Please try again.'
        this.$emit('error', errorMessage)
      } finally {
        this.loading = false
        this.$emit('loading', false)
      }
    },

    decodeJWT(token) {
      try {
        const base64Url = token.split('.')[1]
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split('')
            .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        )
        return JSON.parse(jsonPayload)
      } catch (e) {
        return {}
      }
    },

    async sendToBackend(userData) {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      if (!csrfToken) {
        throw new Error('CSRF token not found. Please refresh the page.')
      }

      const endpoint = this.mode === 'login' ? '/google/login' : '/google/register'

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken,
            'Accept': 'application/json'
          },
          credentials: 'same-origin',
          body: JSON.stringify(userData)
        })

        const data = await response.json()

        if (response.ok && data.success) {
          this.$emit('success', data.message || 'Authentication successful!')

          // Redirect after short delay
          setTimeout(() => {
            window.location.href = data.redirect || '/'
          }, 300)
        } else {
          // Handle validation errors
          if (data.errors) {
            const firstError = Object.values(data.errors)[0]
            throw new Error(Array.isArray(firstError) ? firstError[0] : firstError)
          }
          throw new Error(data.message || 'Authentication failed')
        }
      } catch (error) {
        if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
          throw new Error('Network error. Please check your connection.')
        }
        throw error
      }
    }
  }
}
</script>
